/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../../../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useEffect, useState } from "react";
import { GitMerge, Loader2, Plus } from "lucide-react";
import { toast } from "../../../components/ui/sonner";
import { Panel } from "./V3Header";
import { T } from "../../../lib/i18n";

const MODE_ZH = { sequential: "分开提交 · 先 Planning 后 Building", concurrent: "同步提交 · P+B 同步审查" };

/* 城市默认审批模式：admin / CEO / 设计经理设置；新建项目未显式选择时自动采用 */
export const CityModesPanel = ({ api }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [newCity, setNewCity] = useState("");
  const [newMode, setNewMode] = useState("concurrent");
  const load = () => api.get("/design/v3/settings/city-modes").then(({ data }) => setD(data)).catch(() => setD({ cities: [], canEdit: false }));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const set = async (city, mode) => {
    setBusy(city);
    try { await api.put("/design/v3/settings/city-modes", { city, mode }); toast.success(mode ? T("{0} 默认审批模式：{1}", [city, T(MODE_ZH[mode])]) : T("{0} 已清除默认（按分开提交）", [city])); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };
  if (!d) return null;
  return (
    <Panel tid="dv3-city-modes" title={T("城市默认审批模式（Planning / Building 提交方式）")} icon={GitMerge}
      extra={d.canEdit && (
        <div className="flex flex-wrap items-center gap-1.5">
          <input value={newCity} onChange={(e) => setNewCity(e.target.value)} placeholder={T("城市名，如 Pasadena")} className="h-7 w-36 border border-slate-200 rounded-md px-2 text-xs" data-testid="dv3-city-modes-new-city" />
          <select value={newMode} onChange={(e) => setNewMode(e.target.value)} className="h-7 border border-slate-200 rounded-md px-1.5 text-xs bg-white" data-testid="dv3-city-modes-new-mode"><option value="sequential">{T("分开提交")}</option><option value="concurrent">{T("同步提交")}</option></select>
          <button type="button" disabled={!newCity.trim() || busy} onClick={() => { set(newCity.trim(), newMode); setNewCity(""); }} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-7 px-2 rounded-md bg-slate-900 text-white text-xs inline-flex items-center gap-1 disabled:opacity-40" data-testid="dv3-city-modes-add"><Plus className="h-3 w-3" />{T("添加")}</button>
        </div>
      )}>
      <p className="text-xs text-slate-500 mb-2">{T("有些城市 Planning 与 Building 分开提交（先批 Planning 再交 Building），有些城市同步提交同步审查。在此为每个城市设定默认；新建项目填写城市后自动采用（可在新建时覆盖，项目后期也可改）。未设置的城市默认「分开提交」。同步模式仅合并显示，阶段任务不减。")}</p>
      {d.cities.length === 0 ? <p className="text-xs text-slate-400" data-testid="dv3-city-modes-empty">{T("暂无城市")}</p> : (
        <div className="rounded-lg border border-slate-200 overflow-x-auto">
          <ResponsiveTable className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-2 py-1.5">{T("城市")}</th><th className="text-right px-2 py-1.5">{T("项目数")}</th><th className="text-left px-2 py-1.5">{T("现有项目模式（同步 / 分开）")}</th><th className="text-left px-2 py-1.5">{T("默认审批模式")}</th></tr></thead>
            <tbody>
              {d.cities.map((c) => (
                <tr key={c.key} className="border-t border-slate-100" data-testid={`dv3-city-mode-row-${c.key.replace(/\s+/g, "_")}`} data-mode={c.mode || ""}>
                  <td className="px-2 py-1.5 font-semibold text-slate-800">{c.city}</td>
                  <td className="px-2 py-1.5 text-right text-slate-600">{c.projects}</td>
                  <td className="px-2 py-1.5 text-slate-500"><span className="text-teal-700">{c.concurrent}</span> / <span>{c.sequential}</span></td>
                  <td className="px-2 py-1.5">
                    {d.canEdit ? (
                      <select value={c.mode || ""} disabled={busy === c.city} onChange={(e) => set(c.city, e.target.value || null)} className={`h-7 border rounded-md px-1.5 text-xs ${c.mode === "concurrent" ? "border-teal-300 bg-teal-50 text-teal-800 font-semibold" : c.mode === "sequential" ? "border-slate-300 bg-white font-semibold" : "border-slate-200 bg-white text-slate-500"}`} data-testid={`dv3-city-mode-select-${c.key.replace(/\s+/g, "_")}`}>
                        <option value="">{T("未设置（按分开提交）")}</option>
                        <option value="sequential">{T("分开提交 · 先 Planning 后 Building")}</option>
                        <option value="concurrent">{T("同步提交 · P+B 同步审查")}</option>
                      </select>
                    ) : <span className={c.mode === "concurrent" ? "text-teal-700 font-semibold" : "text-slate-600"}>{c.mode ? T(MODE_ZH[c.mode]) : T("未设置")}</span>}
                    {busy === c.city && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </div>
      )}
    </Panel>
  );
};
